import LegalDocument from '@/components/legal/LegalDocument'

// Base privacy policy. Review with counsel before relying on it.
export default function Privacy({ last_updated }: { last_updated: string }) {
  return (
    <LegalDocument title="Privacy Policy" lastUpdated={last_updated} contactEmail="privacy@getnereus.ai">
      <p>
        This Privacy Policy explains how Nereus ("Nereus", "we", "us") collects, uses, and
        shares information when you use our freight operations platform and related services
        (the "Service"). Nereus is operated by Nereus AI Inc. By using the Service, you
        agree to the practices described here.
      </p>

      <h2>1. Information we collect</h2>
      <p>We collect the following categories of information:</p>
      <ul>
        <li>
          <strong>Account information.</strong> Name, work email address, organization, role,
          and authentication credentials you provide when an account is created.
        </li>
        <li>
          <strong>Connected mailbox data.</strong> When you connect an email inbox (for example
          via Google OAuth or IMAP), we access and store messages, attachments, headers, and
          contacts from that inbox so the Service can read, organize, summarize, and draft
          replies to operational email.
        </li>
        <li>
          <strong>Operational data.</strong> Shipments, bookings, quotes, rate sheets, documents,
          carrier references, and related records you or your counterparties submit.
        </li>
        <li>
          <strong>Usage and device data.</strong> Log data, IP address, browser type, and actions
          taken in the Service, used to operate, secure, and improve it.
        </li>
      </ul>

      <h2>2. How we use information</h2>
      <ul>
        <li>Provide, maintain, and improve the Service.</li>
        <li>Read, classify, summarize, and draft responses to operational email on your behalf.</li>
        <li>Generate quotes and bookings and exchange data with carriers and other third parties you direct us to.</li>
        <li>Authenticate users, secure accounts, and prevent abuse.</li>
        <li>Communicate with you about the Service, including service and support messages.</li>
        <li>Comply with legal obligations and enforce our agreements.</li>
      </ul>

      <h2>3. AI processing</h2>
      <p>
        The Service uses automated and machine-learning systems, including third-party large
        language model providers, to read and generate text from your operational email and
        documents. Content may be transmitted to these providers solely to perform the
        requested processing. We do not sell your data, and we configure providers to not use
        your content to train their general models where that option is available.
      </p>

      <h2>4. How we share information</h2>
      <ul>
        <li>
          <strong>Service providers.</strong> Hosting, storage, email delivery (e.g. Postmark),
          and AI/LLM providers that process data on our behalf under appropriate obligations.
        </li>
        <li>
          <strong>Carriers and counterparties.</strong> Information you direct us to send as part
          of quoting, booking, and document workflows.
        </li>
        <li>
          <strong>Legal and safety.</strong> Where required by law, to protect rights and safety,
          or in connection with a corporate transaction.
        </li>
      </ul>
      <p>We do not sell personal information.</p>

      <h2>5. Data retention</h2>
      <p>
        We retain information for as long as your account is active or as needed to provide the
        Service, comply with legal obligations, resolve disputes, and enforce agreements. You may
        request deletion of your data as described below.
      </p>

      <h2>6. Security</h2>
      <p>
        We use technical and organizational measures to protect your information, including
        encryption of sensitive credentials at rest and access controls. No method of transmission
        or storage is completely secure, and we cannot guarantee absolute security.
      </p>

      <h2>7. Your rights and choices</h2>
      <p>
        Depending on your location, you may have rights to access, correct, export, or delete your
        personal information, and to disconnect a connected mailbox at any time from settings.
        To exercise these rights, contact us at the address below.
      </p>

      <h2>8. International transfers</h2>
      <p>
        We may process and store information in countries other than where you are located. Where
        required, we use appropriate safeguards for such transfers.
      </p>

      <h2>9. Cookies</h2>
      <p>
        We use strictly necessary cookies to keep you signed in and to operate the Service. We do
        not use advertising cookies.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be reflected by updating
        the "Last updated" date above and, where appropriate, by additional notice.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions about this policy or your data? Email{' '}
        <a href="mailto:privacy@getnereus.ai">privacy@getnereus.ai</a>.
      </p>
    </LegalDocument>
  )
}
